import { Dialog, DialogOpenChangeDetails } from "@ark-ui/react";
import { useCallback, useMemo, useState } from "react";
import { Plus, Loader2 } from "lucide-react";
import i18n from "i18next";

import Button from "@/components/button/button";
import CheckCard, {
  StatusLabel,
} from "@/features/widget-forms/forms/weather-model-map/components/check-card/check-card";
import DialogHeader from "@/components/dialog/dailog-header";
import "@/components/dialog/dialog.css";
import Stack from "@/components/stack/stack";
import dialogVariants from "@/components/dialog/dialog-variants.module.css";
import { useTranslation } from "@/lib/i18n";

import { filterPickListItems, uniquePickListDatasets } from "./filter-pick-list-items";
import { SearchFilterPill } from "./search-filter-pill";
import styles from "./modal-styles.module.css";

export interface PickListItem {
  id: string;
  title: string;
  subtitle?: string;
  /** Extra haystack text for search that is not shown. */
  searchText?: string;
  /** Localized dataset source for pills and combined search. */
  datasetLabel?: string;
  /** When set, item is excluded: shown at bottom, disabled, with this status label (e.g. unit mismatch) */
  excluded?: StatusLabel;
  /** When set, item stays in main list but is disabled with this status label (e.g. used in another group) */
  disabledWithLabel?: StatusLabel;
}

export interface PickListModalProps {
  // Header
  title: string;
  description: string;
  addText: string;
  emptyText: string;
  // List
  items: PickListItem[];
  /**
   * Options for the dataset filter pill. Defaults to unique labels found on
   * items. Pass an explicit list to include datasets that currently have no
   * parameters.
   */
  datasetLabels?: string[];
  selected: string[];
  onPick: (id: string) => void;
  /** Status shown when item is already selected. Defaults to "Already added" */
  checkedItemStatusLabel?: StatusLabel;
  /** When true, trigger stays disabled and shows a spinner instead of the plus icon */
  loading?: boolean;
}

function getDefaultCheckedStatus(): StatusLabel {
  return {
    text: i18n.t("admin.widgets.fields.model_map.map_layers.already_added"),
    icon: "check",
  };
}

export const PickListModal = ({
  title,
  description,
  addText,
  emptyText,
  items,
  datasetLabels: datasetLabelsProp,
  selected,
  onPick,
  checkedItemStatusLabel,
  loading = false,
}: PickListModalProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [datasetFilter, setDatasetFilter] = useState<string | undefined>();

  const datasetLabels = useMemo(
    () => datasetLabelsProp ?? uniquePickListDatasets(items),
    [datasetLabelsProp, items],
  );
  const showDatasetFilter = datasetLabels.length > 1;
  const datasetOptions = useMemo(
    () => datasetLabels.map((label) => ({ value: label, label })),
    [datasetLabels],
  );

  const filteredItems = useMemo(
    () => filterPickListItems(items, searchQuery, datasetFilter),
    [items, searchQuery, datasetFilter],
  );

  const { included, excluded, canPickAny } = useMemo(() => {
    const sorted = filteredItems.toSorted((a, b) => {
      const aExcluded = !!a.excluded;
      const bExcluded = !!b.excluded;
      if (aExcluded !== bExcluded) return aExcluded ? 1 : -1;
      return a.title.localeCompare(b.title);
    });
    const inc: PickListItem[] = [];
    const exc: PickListItem[] = [];
    for (const item of sorted) {
      if (item.excluded) exc.push(item);
      else inc.push(item);
    }
    const pickable = items.some(
      (item) => !selected.includes(item.id) && !item.disabledWithLabel && !item.excluded,
    );
    return { included: inc, excluded: exc, canPickAny: pickable };
  }, [filteredItems, items, selected]);

  const resetFilters = useCallback(() => {
    setSearchQuery("");
    setDatasetFilter(undefined);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    resetFilters();
  }, [resetFilters]);

  const handleAddMapLayer = useCallback(
    (id: string) => {
      // Only add if not already selected
      if (!selected.includes(id)) {
        onPick(id);
        setOpen(false);
      }
    },
    [onPick, selected, setOpen],
  );

  const handleOpenChange = useCallback(
    (details: DialogOpenChangeDetails) => {
      setOpen(details.open);
      if (!details.open) resetFilters();
    },
    [resetFilters],
  );

  return (
    <Dialog.Root
      open={open}
      onOpenChange={handleOpenChange}
      onExitComplete={handleClose}
      lazyMount
      unmountOnExit
    >
      <Dialog.Trigger asChild>
        <Button type="button" variant="solid" disabled={loading || !canPickAny}>
          <Stack gap={8} alignItems="center">
            {loading ? (
              <Loader2 size={16} className={styles.iconSpinner} aria-hidden />
            ) : (
              <Plus size={16} />
            )}
            <span>{addText}</span>
          </Stack>
        </Button>
      </Dialog.Trigger>
      <Dialog.Backdrop className={styles.nestedModalBackdrop} />
      <Dialog.Positioner className={styles.nestedModalPositioner}>
        <Dialog.Content className={dialogVariants.default}>
          <DialogHeader.Root>
            <Dialog.Title>{title}</Dialog.Title>
            <Dialog.Description>{description}</Dialog.Description>
          </DialogHeader.Root>
          <div className={styles.searchControls}>
            <div className={styles.searchField}>
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={t(
                  "admin.widgets.fields.model_map.pick_list.search_placeholder",
                )}
                aria-label={t(
                  "admin.widgets.fields.model_map.pick_list.search_placeholder",
                )}
                className={styles.searchInput}
              />
            </div>
            {showDatasetFilter && (
              <div
                className={styles.searchFilters}
                role="group"
                aria-label={t("admin.widgets.fields.model_map.pick_list.filter_dataset")}
              >
                <SearchFilterPill
                  typeLabel={t(
                    "admin.widgets.fields.model_map.pick_list.filter_type_dataset",
                  )}
                  clearLabel={t("admin.widgets.fields.model_map.pick_list.filter_none")}
                  options={datasetOptions}
                  value={datasetFilter}
                  onChange={setDatasetFilter}
                />
              </div>
            )}
          </div>
          <div className={styles.modalGridContainer}>
            <div className={styles.modalGrid}>
              {included.length > 0 || excluded.length > 0 ? (
                <>
                  {included.map((item) => {
                    const isChecked = selected.includes(item.id);
                    const isDisabledByLabel = !!item.disabledWithLabel;
                    return (
                      <CheckCard
                        key={item.id}
                        title={item.title}
                        checked={isChecked}
                        disabled={isDisabledByLabel}
                        statusLabel={
                          isChecked
                            ? (checkedItemStatusLabel ?? getDefaultCheckedStatus())
                            : item.disabledWithLabel
                        }
                        onClick={() => handleAddMapLayer(item.id)}
                      >
                        {item.subtitle && (
                          <span className={styles.subtitle}>{item.subtitle}</span>
                        )}
                      </CheckCard>
                    );
                  })}
                  {excluded.length > 0 && (
                    <>
                      {included.length > 0 && <div className={styles.excludedSeparator} />}
                      {excluded.map((item) => (
                        <CheckCard
                          key={item.id}
                          title={item.title}
                          disabled
                          statusLabel={item.excluded}
                        >
                          {item.subtitle && (
                            <span className={styles.subtitle}>{item.subtitle}</span>
                          )}
                        </CheckCard>
                      ))}
                    </>
                  )}
                </>
              ) : (
                <div className={styles.modalEmpty}>
                  {searchQuery.trim() || datasetFilter
                    ? t("admin.widgets.fields.model_map.pick_list.search_no_results")
                    : emptyText}
                </div>
              )}
            </div>
          </div>
          <div className={styles.footer}>
            <Stack justifyContent="flex-end" gap={8}>
              <Button type="button" variant="ghost" onClick={handleClose}>
                {t("general.cancel")}
              </Button>
            </Stack>
          </div>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};
